import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReactGA from "react-ga4";

import { ModuleNameHr, CategoryNameHr, CategoryName } from "../../models";
import { useMakes } from "../../routes/Configuration/hooks";
import { findMake } from "../../routes/Configuration/helpers";
import { SimilarModelPair } from "../../typings/interfaces";
import styles from "./makeModelInfoCta.scss";

interface MakeModelInfoCtaProps {
  module: string;
  category: string;
  // BE-supplied primary (make, model) of the matched info entry. Non-null
  // means an info page exists — either for the ad's own (make, model) or via
  // a variant, in which case the pair here points to the entry's primary so
  // the URL still resolves. Null/undefined = hide the CTA.
  infoPage?: SimilarModelPair | null;
}

const MakeModelInfoCta = (props: MakeModelInfoCtaProps) => {
  const { module, category, infoPage } = props;

  const isOsobniAutomobili =
    module === ModuleNameHr.vehicles && category === CategoryNameHr.car;

  const infoMakeId = infoPage && infoPage.makeId;
  const infoModelId = infoPage && infoPage.modelId;
  const hasInfo = Boolean(isOsobniAutomobili && infoMakeId && infoModelId);

  // Car makes (with models) are fetched only when the CTA can be shown
  const makes = useMakes(hasInfo ? CategoryName.car : undefined);

  if (!hasInfo) {
    return null;
  }

  const make = findMake(makes, infoMakeId);
  const model = make && (make.models || []).find(m => m.id === infoModelId);
  const makeSlug = make ? make.route || null : null;
  const modelSlug = model ? model.route || null : null;

  if (!makeSlug || !modelSlug) {
    return null;
  }

  const href = `/${ModuleNameHr.vehicles}/${CategoryNameHr.car}/${makeSlug}/${modelSlug}/info`;

  const handleClick = () => {
    ReactGA.event({
      category: "Informacije o modelu",
      action: "Click",
      label: "Klik na više informacija"
    });
  };

  return <MakeModelInfoCtaView href={href} onClick={handleClick} />;
};

// Split so the visibility-impression observer can hook a stable ref that only
// exists once we've resolved to the rendered CTA (all guards above have passed).
const MakeModelInfoCtaView = (props: { href: string; onClick: () => void }) => {
  const { href, onClick } = props;
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          ReactGA.event({
            category: "Informacije o modelu",
            action: "View",
            label: "Prikazan button u oglasu"
          });
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={styles.box}>
      <div className={styles.title}>Više informacija o ovom modelu</div>
      <div className={styles.description}>
        Pogledajte osnovne informacije, najčešće motore, prednosti, nedostatke i savjete za kupnju ovog modela.
      </div>
      <Link to={href} className={styles.cta} onClick={onClick}>
        Saznaj više o modelu
      </Link>
    </div>
  );
};

export default MakeModelInfoCta;



// WEBPACK FOOTER //
// ./src/components/MakeModelInfoCta/index.tsx